import { useMemo } from 'react';
import { useAuthenticationState } from 'app/authentication/state';
import { UserRole, hasRole } from 'app/authentication/UserRole';

/**
 * Hook to determine if the current user can modify (start/end) an installation.
 *
 * Rules:
 * - Admin users (cf_admin) can always modify
 * - If no installation is ongoing, any user with access can start
 * - If installation is ongoing, only the installer who started it can end it
 *
 * @param createdBy - The user ID of who created/started the work session (null if no active session)
 * @returns Object containing canModify boolean and reason string
 */
export const useCanModifyInstallation = (createdBy: string | null) => {
  const { userId, roles } = useAuthenticationState();

  const isAdmin = useMemo(() => hasRole(roles, UserRole.Admin), [roles]);

  const canModify = useMemo(() => {
    // Admins can always modify
    if (isAdmin) return true;

    // If no active session (createdBy is null), anyone can start a new installation
    if (createdBy === null) return true;

    // If there's an active session, only the creator can modify (end) it
    return userId === createdBy;
  }, [isAdmin, createdBy, userId]);

  return {
    canModify,
    isAdmin,
  };
};
